import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactPaginate from "react-paginate";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faEdit, faHeart, faTrash} from '@fortawesome/free-solid-svg-icons';
import Swal from "sweetalert2";
import {Link} from "react-router-dom";
import LoadingSpinner from "./LoadingSpinner";
import api from "../api";

function SurveyComments({ isAuthenticated, isCommented, isVoted, surveyId, token, currentUserId, isBanned, bannedEndDate, isAdmin }) {
    const { t } = useTranslation();

    const [comments, setComments] = useState([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [loadingComments, setLoadingComments] = useState(false);
    const [likedComments, setLikedComments] = useState(new Set());
    const [newComment, setNewComment] = useState('');
    const [error, setError] = useState(null);
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editingText, setEditingText] = useState('');
    const [isCommentedLocal, setIsCommentedLocal] = useState(false);
    const [dailyCount, setDailyCount] = useState(0);

    const totalPages = Math.ceil(totalCount / limit);

    const loadComments = async (pageNumber) => {
        setLoadingComments(true);
        setError(null);
        try {
            const headers = {};
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }

            const response = await api.get(
                `${process.env.REACT_APP_API_URL}/api/survey/${surveyId}/comments?page=${pageNumber}&limit=${limit}`,
                { headers }
            );

            setComments(response.data.comments);
            setTotalCount(response.data.count || 0);

            updateDailyCount(response.data.comments);
        } catch (err) {
            setError(t('survey.errorFetchingComments'));
        } finally {
            setLoadingComments(false);
        }
    };

    const updateDailyCount = (loadedComments) => {
        if (!currentUserId) return;
        const now = Date.now();
        const oneDayAgo = now - (24 * 60 * 60 * 1000);

        const userCommentsLastDay = loadedComments.filter((c) => {
            if (c.user_id !== currentUserId) return false;
            const commentTime = new Date(c.created_at).getTime();
            return commentTime >= oneDayAgo;
        });

        const count = userCommentsLastDay.length;
        setDailyCount(count);
        setIsCommentedLocal(count >= 5);
    };

    useEffect(() => {
        loadComments(page);
    }, [page, limit]);

    const handleAddComment = async () => {
        if (!isAuthenticated) {
            Swal.fire({
                text: t('error.loginRequired'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('survey.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }

        if (isBanned) {
            const banMessage = bannedEndDate
                ? t('survey.userBannedUntil', { date: new Date(bannedEndDate).toLocaleString() })
                : t('survey.userBanned');

            Swal.fire({
                text: banMessage,
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('survey.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
            return;
        }

        if (!newComment.trim()) return;

        if (!isVoted) {
            Swal.fire({
                text: t('survey.mustVoteToComment'),
                icon: 'error',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#ff0000',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('survey.ok'),
            });
            return;
        }

        try {
            const response = await api.post(
                `${process.env.REACT_APP_API_URL}/api/survey/comments`,
                { text: newComment },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            setComments((prev) => [response.data.comment, ...prev]);
            setTotalCount(prevCount => prevCount + 1);
            setNewComment('');

            const newCount = dailyCount + 1;
            setDailyCount(newCount);
            setIsCommentedLocal(newCount >= 5);

            Swal.fire({
                text: t('survey.commentAdded'),
                icon: 'success',
                background: '#1a1a1a',
                color: '#b3b3b3',
                iconColor: '#00ff00',
                confirmButtonColor: '#3085d6',
                confirmButtonText: t('survey.ok'),
                customClass: {
                    popup: 'swal2-dark-popup',
                    confirmButton: 'swal2-dark-button'
                }
            });
        } catch (err) {
            if (err.response && err.response.data && err.response.data.error) {
                const apiError = err.response.data.error;
                if (apiError.includes('Comment too long')) {
                    Swal.fire({
                        text: t('survey.commentTooLong'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonColor: '#d33',
                        confirmButtonText: t('survey.ok'),
                        customClass: {
                            popup: 'swal2-dark-popup',
                            confirmButton: 'swal2-dark-button'
                        }
                    });
                } else if (apiError === 'You have reached the daily limit of 5 comments') {
                    Swal.fire({
                        text: t('survey.dailyLimitReached'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonColor: '#d33',
                        confirmButtonText: t('survey.ok'),
                        customClass: {
                            popup: 'swal2-dark-popup',
                            confirmButton: 'swal2-dark-button'
                        }
                    });
                    setDailyCount(5);
                    setIsCommentedLocal(true);
                } else if (apiError === 'You must vote before commenting') {
                    Swal.fire({
                        text: t('survey.mustVoteToComment'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonColor: '#d33',
                        confirmButtonText: t('survey.ok'),
                        customClass: {
                            popup: 'swal2-dark-popup',
                            confirmButton: 'swal2-dark-button'
                        }
                    });
                } else {
                    setError(t('survey.errorAddingComment'));
                }
            } else {
                setError(t('survey.errorAddingComment'));
            }
        }
    };

    const handleLikeComment = (commentId) => {
        const accessToken = localStorage.getItem('access_token');
        if (!accessToken) {
            return;
        }

        if (likedComments.has(commentId)) {
            api.delete(`${process.env.REACT_APP_API_URL}/api/survey/comments/${commentId}/like`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            })
                .then(() => {
                    setLikedComments((prev) => {
                        const newLikedComments = new Set(prev);
                        newLikedComments.delete(commentId);
                        return newLikedComments;
                    });
                    setComments((prevComments) =>
                        prevComments.map((comment) =>
                            comment.id === commentId ? { ...comment, likes: comment.likes - 1 } : comment
                        )
                    );
                })
                .catch((error) => console.error('Error removing like:', error));
        } else {
            api.post(`${process.env.REACT_APP_API_URL}/api/survey/comments/${commentId}/like`, {}, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            })
                .then(() => {
                    setLikedComments((prev) => {
                        const newLikedComments = new Set(prev);
                        newLikedComments.add(commentId);
                        return newLikedComments;
                    });
                    setComments((prevComments) =>
                        prevComments.map((comment) =>
                            comment.id === commentId ? { ...comment, likes: comment.likes + 1 } : comment
                        )
                    );
                })
                .catch((error) => console.error('Error adding like:', error));
        }
    };

    const handleDeleteComment = async (commentId, isUserComment) => {
        const result = await Swal.fire({
            text: t('survey.confirmDeleteComment'),
            icon: 'warning',
            showCancelButton: true,
            background: '#1a1a1a',
            color: '#b3b3b3',
            iconColor: '#ff0000',
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('survey.delete'),
            cancelButtonText: t('survey.cancel'),
            customClass: {
                popup: 'swal2-dark-popup',
                confirmButton: 'swal2-dark-button'
            }
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            const headers = { Authorization: `Bearer ${token}` };
            const response = await api.delete(
                `${process.env.REACT_APP_API_URL}/api/survey/comments/${commentId}`,
                { headers }
            );

            if (response.data.status === 'success') {
                const updatedComments = comments.filter((c) => c.id !== commentId);
                setComments(updatedComments);
                setTotalCount((prevCount) => prevCount - 1);

                if (!isAdmin && isUserComment && dailyCount > 0) {
                    const newCount = dailyCount - 1;
                    setDailyCount(newCount);
                    setIsCommentedLocal(newCount >= 5);
                }

                updateDailyCount(updatedComments);

                Swal.fire({
                    text: t('survey.commentDeleted'),
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#00ff00',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: t('survey.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            } else {
                Swal.fire({
                    text: t('survey.errorDeletingComment'),
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#ff0000',
                    confirmButtonColor: '#d33',
                    confirmButtonText: t('survey.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            }
        } catch (err) {
            if (err.response && err.response.data && err.response.data.error) {
                const apiError = err.response.data.error;
                if (apiError === 'You cannot delete comments from other users') {
                    Swal.fire({
                        text: t('survey.cannotDeleteOthersComments'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonColor: '#d33',
                        confirmButtonText: t('survey.ok'),
                        customClass: {
                            popup: 'swal2-dark-popup',
                            confirmButton: 'swal2-dark-button'
                        }
                    });
                } else if (apiError === 'Comment not found') {
                    Swal.fire({
                        text: t('survey.commentNotFound'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonColor: '#d33',
                        confirmButtonText: t('survey.ok'),
                        customClass: {
                            popup: 'swal2-dark-popup',
                            confirmButton: 'swal2-dark-button'
                        }
                    });
                } else if (apiError === 'Unauthorized') {
                    Swal.fire({
                        text: t('error.loginRequired'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonColor: '#3085d6',
                        confirmButtonText: t('survey.ok'),
                        customClass: {
                            popup: 'swal2-dark-popup',
                            confirmButton: 'swal2-dark-button'
                        }
                    });
                } else {
                    Swal.fire({
                        text: t('survey.errorDeletingComment'),
                        icon: 'error',
                        background: '#1a1a1a',
                        color: '#b3b3b3',
                        iconColor: '#ff0000',
                        confirmButtonColor: '#d33',
                        confirmButtonText: t('survey.ok'),
                        customClass: {
                            popup: 'swal2-dark-popup',
                            confirmButton: 'swal2-dark-button'
                        }
                    });
                }
            } else {
                Swal.fire({
                    text: t('survey.errorDeletingComment'),
                    icon: 'error',
                    background: '#1a1a1a',
                    color: '#b3b3b3',
                    iconColor: '#ff0000',
                    confirmButtonColor: '#d33',
                    confirmButtonText: t('survey.ok'),
                    customClass: {
                        popup: 'swal2-dark-popup',
                        confirmButton: 'swal2-dark-button'
                    }
                });
            }
        }
    };

    const saveEditedComment = async () => {
        const accessToken = localStorage.getItem('access_token');
        try {
            await api.put(
                `${process.env.REACT_APP_API_URL}/api/survey/comments/${editingCommentId}`,
                { text: editingText },
                {
                    headers: { Authorization: `Bearer ${accessToken}` },
                }
            );
            setComments((prev) => prev.map(comment =>
                comment.id === editingCommentId ? { ...comment, text: editingText } : comment
            ));
            setEditingCommentId(null);
            setEditingText('');
        } catch (err) {
        }
    };

    const handleEditComment = (commentId) => {
        const commentToEdit = comments.find(comment => comment.id === commentId);
        if (commentToEdit) {
            setEditingCommentId(commentId);
            setEditingText(commentToEdit.text);
        }
    };

    const formatDate = (isoDate) => {
        if (!isoDate) return '';
        const date = new Date(isoDate);
        return date.toLocaleDateString('en', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    const handlePageClick = (event) => {
        const newPage = event.selected + 1;
        setPage(newPage);
    };

    return (
        <aside className="comments-page">
            <h2 className="comments-title">
                <div className="comments-title-info">
                    <span className="comment-title">
                        {t('survey.comments')}
                    </span>
                    {isAuthenticated && isVoted && (
                        <span className="comments-limit">
                            {t('survey.dailyLimitInfo', { dailyCount, left: 5 - dailyCount })}
                        </span>
                    )}
                </div>
            </h2>

            {isAuthenticated && isVoted && (
                <>
                    {editingCommentId ? (
                        <div className="add-comment">
                            <textarea
                                className="comment-textarea"
                                value={editingText}
                                onChange={(e) => setEditingText(e.target.value)}
                                placeholder={t('survey.leaveComment')}
                            ></textarea>
                            <button
                                className="submit-comment-button"
                                onClick={saveEditedComment}
                            >
                                {t('survey.save')}
                            </button>
                        </div>
                    ) : (
                        !isCommentedLocal && (
                            <div className="add-comment">
                                <textarea
                                    className="comment-textarea"
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder={t('survey.leaveComment')}
                                ></textarea>
                                <button
                                    className="submit-comment-button"
                                    onClick={handleAddComment}
                                >
                                    {t('survey.submitComment')}
                                </button>
                            </div>
                        )
                    )}
                </>
            )}

            {error && <p className="error-message">{error}</p>}

            <div className="comments-list">
                {loadingComments ? (
                    <LoadingSpinner className="comments-loading-spinner"/>
                ) : comments.length > 0 ? (
                    comments.map((comment) => {
                        const isUserComment = currentUserId && comment.user_id === currentUserId;
                        const canEditDelete = isAdmin || isUserComment;

                        return (
                            <div key={comment.id} className="comment-item">
                                <div className="comment-data">
                                    <span className="comment-username">
                                        <Link to={`/user/${comment.user_id}`} className="comment-username">
                                            {comment.username}
                                        </Link>
                                    </span>
                                    <span className="comment-date">{formatDate(comment.created_at)}</span>
                                </div>
                                <div className="comment-text">{comment.text}</div>
                                <div className="comment-actions-wrapper">
                                <span
                                    className={`like-comment-button ${likedComments.has(comment.id) ? 'liked' : ''}`}
                                    onClick={() => handleLikeComment(comment.id)}
                                    style={{ cursor: isAuthenticated ? 'pointer' : 'default' }}
                                    title={likedComments.has(comment.id) ? t('survey.unlike') : t('survey.like')}
                                >
                                    <FontAwesomeIcon icon={faHeart} className="icon"/>
                                    <span className="like-count">{comment.likes}</span>
                                </span>
                                    {canEditDelete && (
                                        <>
                                            <div className="comment-action">
                                                <FontAwesomeIcon
                                                    icon={faEdit}
                                                    className="edit-comment-icon"
                                                    onClick={() => handleEditComment(comment.id)}
                                                    title={t('survey.editComment')}
                                                />
                                            </div>
                                            <div className="comment-action">
                                                <FontAwesomeIcon
                                                    icon={faTrash}
                                                    className="delete-comment-icon"
                                                    onClick={() => handleDeleteComment(comment.id, isUserComment)}
                                                    title={t('survey.deleteComment')}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="no-comments">{t('survey.noComments')}</div>
                )}
            </div>
            {totalPages > 1 && !loadingComments && (
                <ReactPaginate
                    breakLabel="..."
                    nextLabel={null}
                    onPageChange={handlePageClick}
                    pageRangeDisplayed={1}
                    marginPagesDisplayed={2}
                    pageCount={totalPages}
                    previousLabel={null}
                    forcePage={page - 1}
                    containerClassName="pagination-container"
                    pageClassName="pagination-item"
                    pageLinkClassName="pagination-link"
                    previousClassName="pagination-item"
                    nextClassName="pagination-next"
                    previousLinkClassName="pagination-previous"
                    nextLinkClassName="pagination-link"
                    breakClassName="pagination-item"
                    breakLinkClassName="pagination-link"
                    activeClassName="active"
                />
            )}
        </aside>
    );
}

export default SurveyComments;
